import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { botApi } from '../services/botApi'

// Автоопределение языка из Telegram: язык приложения «русский» → 'ru',
// любой другой → 'en'. persist поверх перезапишет сохранённый выбор.
// Если данных Telegram нет (SDK не загрузился — как раз случай, когда юзер
// видит экран сбоя загрузки), берём язык устройства: иначе русскоязычный
// пользователь получил бы экран ошибки на английском.
function detectInitialLanguage() {
  try {
    const code = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code
    if (code) return code === 'ru' ? 'ru' : 'en'
  } catch (e) { /* приватный режим — падаем на язык устройства */ }
  try {
    return String(navigator.language || '').toLowerCase().startsWith('ru') ? 'ru' : 'en'
  } catch (e) {
    return 'en'
  }
}

// ── Безопасное хранилище для persist (п.3 task.txt: «иногда не открывается») ──
// zustand/persist оборачивает КАЖДЫЙ экшн так, что после set() вызывается
// storage.setItem(); если setItem бросает, исключение вылетает из экшна и
// добивает рендер (ErrorBoundary / пустой экран). Проверено на живом сторе:
// при localStorage.setItem, кидающем QuotaExceededError (приватный режим,
// переполнение — типично для iOS) или SecurityError (хранилище запрещено в
// webview), падали ВСЕ экшны — приложение не открывалось именно у этих юзеров.
// Здесь хранилище никогда не бросает: getItem → null, setItem/removeItem → no-op.
// Цена — настройки не переживают перезапуск на таких устройствах (нормальная
// деградация), зато приложение открывается всегда.
const safeStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name)
    } catch (e) {
      return null
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value)
    } catch (e) { /* хранилище недоступно/переполнено — молча пропускаем */ }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name)
    } catch (e) { /* см. выше */ }
  },
}

export const useDashboardStore = create(
  persist(
    (set, get) => ({
      period: 'daily',
      activeTab: 'dashboard',
      activeModal: null,
      isModalOpen: false,
      language: detectInitialLanguage(),
      // Уведомления юзера (бот шлёт их по этим флагам). report — ежедневный
      // отчёт (управляется на бэкенде, в настройках не показывается); funding —
      // «Уведомления о фандингах» за 5 минут до выплаты (п.8 task.txt).
      notifications: { trades: true, report: true, funding: true },
      activeBotsTab: 'spot',
      botsMainTab: 'activeBots',
      // minFundingRate — верхняя граница фандинга (бот берёт ставки НЕ ВЫШЕ неё).
      // Было '0.8': с положительным порогом в отбор попадали монеты с
      // положительным фандингом, где лонг платит фандинг вместо того, чтобы получать.
      botsConfig: { timeBeforeFunding: '10m', exchange: 'bybit', minFundingRate: '-0.6', blacklistedPairs: [], maxPositions: 3 },
      pinnedSignals: [],
      userBots: [],
      botsLoaded: false, // NEW: true только после первого успешного ответа сервера
      subscriptionEnd: null,
      hasApiKeys: false,
      // Доступность типов счёта по подписке (из /api/users/me).
      demoAllowed: false,
      realAllowed: false,
      // Подписка была, но истекла (из /api/users/me → subscription_expired).
      subscriptionExpired: false,
      // Пробный виртуальный бот (Волна 1): сколько закрытых виртуальных
      // сделок уже использовано (из /api/users/me → virtual_orders_used/limit).
      // После лимита показываем плашку оплаты.
      virtualOrdersUsed: 0,
      virtualOrdersLimit: 2,
      // Подключены ли ключи конкретного типа счёта (из /api/exchanges/has-keys).
      hasRealKeys: false,
      hasDemoKeys: false,
      // Конфиг приложения из /api/app-config (feature-флаги, биржи, оплаты,
      // «Тех. работы»). Грузится при каждом открытии приложения, НЕ персистится.
      appConfig: {
        exchanges: { bybit: true, binance: true, okx: false },
        payments:  { usdt_trc20: true, usdt_bep20: true, usdt_sol: true, ton: true, eth: false },
        features:  { signals: true, bots: true, diary: true, leaderboard: true },
        maintenance: false,
      },
      // Админ ли текущий пользователь (id в ADMIN_IDS/ADMIN_ID на бэкенде).
      // По нему показывается вкладка «Админ».
      isAdmin: false,
      // Система подсказок (онбординг-тур). Хранится в localStorage:
      // hintsEnabled — показывать подсказки вообще;
      // hintsDone — тур завершён (автозапуск не сработает);
      // hintsIntroSeen — приветствие-карточка уже показана.
      hintsEnabled: true,
      hintsDone: false,
      hintsIntroSeen: false,

      setPeriod: period => set({ period }),
      setActiveTab: tab => set({ activeTab: tab }),
      setActiveModal: modal => set({ activeModal: modal, isModalOpen: !!modal }),      setIsModalOpen: open => set({ isModalOpen: open }),
      setLanguage: lang => set({ language: lang }),
      setNotifications: notifications => set({ notifications }),
      setActiveBotsTab: tab => set({ activeBotsTab: tab }),
      setBotsMainTab: tab => set({ botsMainTab: tab }),
      setBotsConfig: cfg => set(s => ({ botsConfig: { ...s.botsConfig, ...cfg } })),
      // userBots может прийти как массив (getBots, BotCard) ИЛИ как функция
      // (функциональное обновление prev => ... из handleAddClick виртуального
      // бота). Если просто положить функцию в set({userBots}), zustand НЕ
      // вызовет её (он вызывает только если весь partial — функция) — в сторе
      // окажется функция вместо массива, и следующий же рендер BotsPage упадёт
      // с «userBots.find is not a function» (ErrorPage «Попробовать снова»).
      setUserBots: bots => set(s => ({
        userBots: typeof bots === 'function' ? bots(s.userBots) : bots,
      })),
      setSubscriptionEnd: end => set({ subscriptionEnd: end }),
      setSubscriptionExpired: expired => set({ subscriptionExpired: !!expired }),
      setHasApiKeys: (hasApiKeys) => set({ hasApiKeys }),
      setAccessFlags: ({ demo_allowed, real_allowed }) => set({
        demoAllowed: !!demo_allowed,
        realAllowed: !!real_allowed,
      }),
      setPerTypeKeys: ({ hasRealKeys, hasDemoKeys }) => set({
        hasRealKeys: !!hasRealKeys,
        hasDemoKeys: !!hasDemoKeys,
      }),
      setVirtualOrders: ({ used, limit }) => set({
        virtualOrdersUsed: used ?? 0,
        virtualOrdersLimit: limit ?? 2,
      }),
      setAppConfig: cfg => set({ appConfig: cfg }),
      setIsAdmin: isAdmin => set({ isAdmin: !!isAdmin }),

      // ── Подсказки (онбординг-тур) ──────────────────────────────────────────
      // Включение/выключение. Ключевое поведение: выключили → тур не запускается
      // сам (hintsDone=true); включили ЗАНОВО → тур должен пройти ещё раз с начала
      // (hintsDone=false). Если значение не изменилось — состояние не трогаем.
      setHintsEnabled: enabled => set(s => {
        const on = !!enabled
        if (on === s.hintsEnabled) return {}
        if (!on) return { hintsEnabled: false, hintsDone: true }
        return { hintsEnabled: true, hintsDone: false }
      }),
      setHintsIntroSeen: seen => set({ hintsIntroSeen: !!seen }),
      // Ручной запуск тура (кнопка на приветствии / «Показать подсказки» в настройках).
      startHintsTour: () => set({ hintsEnabled: true, hintsDone: false, hintsIntroSeen: true }),
      // Завершение тура: прошёл до конца или нажал «Пропустить».
      finishHintsTour: () => set({ hintsDone: true }),
      togglePinnedSignal: id => set(s => ({
        pinnedSignals: s.pinnedSignals.includes(id)
          ? s.pinnedSignals.filter(p => p !== id)
          : [...s.pinnedSignals, id],
      })),

      // Загрузка ботов с сервера
      fetchUserBots: async () => {
        try {
          const bots = await botApi.getBots();
          console.log('[STORE] Fetched bots:', bots);

          const normalizedBots = (Array.isArray(bots) ? bots : []).map(bot => ({
            ...bot,
            accountMode: bot.accountMode || bot.account_mode ||
              (bot.params?.DEMO === "True" || bot.params?.DEMO === true ? 'demo' : 'real'),
            algoParams: bot.algoParams || {
              leverage: bot.params?.Leverage || 2,
              globalVolume: bot.params?.Volume || 100,
              tp: bot.params?.TakeProfit || 0.3,
              sl: bot.params?.StopLoss || 0.1,
              orders: bot.params?.Token || 1,
              tradingMode: 'safe'
            }
          }));

          // botsLoaded=true выставляем ТОЛЬКО при успехе.
          // Если запрос упадёт — флаг останется false, и UI будет
          // считать, что данные не загружены (кнопки останутся заблокированы,
          // это безопаснее, чем случайно разрешить редактирование).
          set({ userBots: normalizedBots, botsLoaded: true });
        } catch (err) {
          console.error('[STORE] Failed to fetch bots:', err);
        }
      },

      addUserBot: bot => set(s => ({ userBots: [...s.userBots, bot] })),
      toggleUserBot: id => set(s => ({
        userBots: s.userBots.map(b =>
          b.id === id ? { ...b, status: b.status === 'active' ? 'stopped' : 'active' } : b
        ),
      })),
      removeUserBot: id => set(s => ({ userBots: s.userBots.filter(b => b.id !== id) })),
      updateUserBot: (id, updates) => set(s => ({ userBots: s.userBots.map(b => b.id === id ? { ...b, ...updates } : b) })),
      disableAllBots: type => set(s => ({
        userBots: s.userBots.map(b => b.type === type ? { ...b, status: 'paused' } : b),
      })),
    }),
    {
      name: 'scalpex-settings',
      storage: createJSONStorage(() => safeStorage),
      partialize: state => ({
        language: state.language,
        notifications: state.notifications,
        activeBotsTab: state.activeBotsTab,
        botsConfig: state.botsConfig,
        pinnedSignals: state.pinnedSignals,
        hasApiKeys: state.hasApiKeys,
        hintsEnabled: state.hintsEnabled,
        hintsDone: state.hintsDone,
        hintsIntroSeen: state.hintsIntroSeen,
        // userBots и botsLoaded НЕ персистим намеренно —
        // при каждом свежем открытии приложения список ботов
        // должен запрашиваться с сервера заново.
      }),
    }
  )
)